import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api, JnlView } from '../../utils/api';
import { statutoryBookName } from '../../utils/countryFeatures';
import DlBtn from '../DlBtn';
import Pagination, { getPageSize } from '../Pagination';
import type { AwCtx } from './ctx';
import { baht, btn, card, dmy, errBox, inp, note, num, openBlob, pillAmber, pillGreen, pillGrey, pillRed, table, td, th } from './ui';
import { Dialog, Loader, chip, chipOn, emptyLine } from './dialog';
import { SourceDocOpen } from './sourceDoc';
import { useReadOnlySeat } from '../../utils/readOnlySeat';
import { isReadOnlySeat } from './panes/fixedAssets/shared';
import { ReverseReasonDialog } from './reverseReason';
import { REV_REF_KEY } from './docEntries';
import { NoCell } from './listCells';
import { hasRecordWindow } from './relatedDocs';
import { RecordEvidence } from './attachments';   // owner 2026-11-02: the voucher's supporting files and linked records
import DraftEditDialog from '../Bookkeeping/DraftEditDialog';
import type { JnlDraftEditVerdict } from '../../types';

/**
 * COA-C2 / C5 / C6 / C8 / C10 / C11 / D3 — one journal, read on screen (owner 2026-10-19).
 *
 * The customer's complaint, in the plainest words of any document they sent: clicking "journal"
 * downloads a PDF instead of showing them anything. This is the screen that answers it. A card in
 * Accounting › Journal entries now OPENS its book here — the paged entry list from
 * GET /api/journal/entries — and the PDF is still one click away, in the toolbar.
 *
 * ── The same complaint, one level down (C5 · C6 · C8 · D3) ──────────────────────────────────────
 * A list of vouchers you cannot open is the same failure in miniature, and
 * GET /api/journal/entries/:entryId had been serving ONE entry with its lines for weeks with no
 * caller anywhere in the client — 没界面=没做. A row (and the menu beside it) now opens that entry
 * in the full-screen window: every line with the chart's own account code and name, both totals and
 * whether they agree, and the reversal relationship the server resolved. Read-only, always: see the
 * rules written on EntryDetailDialog and RowActions below.
 * (EXCEPTION, owner 2026-11-02, "Daily Journals Structure": no row click any more. A row carries two links, its Journal No.
 * and its Source Document No., as the workspace's other lists do; see SourceCell's note below.)
 *
 * ── ⛔ THE TWO EXPORT EXITS, AND THE RULE THAT KEEPS THEM APART ─────────────────────────────────
 * This page carries two different files and a reader must never confuse them. Two more requirement
 * groups will copy this pattern, so the rule is written here rather than left to be inferred:
 *
 *   1. THE STATUTORY BOOK — `GET /ledger-books/journal/:kind`. The whole volume, in the form the
 *      DBD requires, and the same renderer serves the client portal and the audit pack. It takes
 *      NO filter: a "statutory book" that silently held only what somebody had filtered to is a
 *      false document. On screen it is a BOOK icon and it carries the book's own statutory Thai
 *      name (statutoryBookName) followed by "the whole book, as filed".
 *   2. THE CURRENT VIEW — Excel and PDF of exactly what this screen is showing: the chosen book,
 *      the period, and every filter. It is a working paper, never a statutory book. On screen it
 *      sits in its own group behind the words "this view", separated by a rule, and its buttons are
 *      spreadsheet/PDF icons.
 *
 * So: grouped apart, separated by a rule, worded apart ("the whole book, as filed" vs "this view"),
 * and different icons.
 * ⚠️ NOT by what the browser does with the file. The server does mark them differently — the
 * statutory route answers `Content-Disposition: inline` and journal-lines.pdf `attachment` — but
 * BOTH buttons here go through ui.ts openBlob, which fetches the response as a blob and
 * window.open()s an object URL, throwing the header away; a blob-URL PDF lands in Downloads in most
 * browsers either way. So the four differences above are the whole of what a reader actually sees,
 * and the rule the next two requirement groups copy must not count a fifth. A one-line note says
 * it in words, because nobody should have to click to find out which file they are about to hand an
 * auditor. The filters themselves are built in ONE place — `api.jnlViewQs` — so the list and both
 * exports cannot interpret them differently (see the comment there).
 *
 * ⛔ A BANK BOOK IS ONE VOLUME PER ACCOUNT NUMBER. That is why /available returns a card per
 * account, and why the card's own account rides in the view (JnlView.bankAccountId) so the list and
 * both exports read that volume and no other. services/ledgerBooks.ts goes further and REFUSES to
 * render a bank book that names no account; this screen cannot reach that state, because every bank
 * card comes from /available already carrying one.
 */
export type Book = { kind: string; label: string; bankAccountId: string; entries: number;
  /** One of the five daily journals (owner 2026-11-02, server/src/services/journalTypes.ts): this card opens that journal's
   *  list, read across the statutory books, and `kind` is ''. Absent on a statutory volume's card, which lists by `kind`. */
  journal?: string };

/** The five, in the requirement's order: the server's JOURNAL_TYPES, which decides what is in each. */
export const JOURNAL_CODES = ['PJ', 'CPJ', 'SJ', 'CRJ', 'GJ'] as const;
/** ⛔ A label, not a statutory name: the country pack names the five books (statutoryBookName), and these five are a way of
 *  reading them, so they live in i18n. The prefix is the requirement's own, the same in every language. */
export const JOURNAL_KEY: Record<string, string> = {
  PJ: 'aw_jl_type_pj', CPJ: 'aw_jl_type_cpj', SJ: 'aw_jl_type_sj', CRJ: 'aw_jl_type_crj', GJ: 'aw_jl_type_gj',
};
/** "Support both cash and bank transactions within the Cash Receipts and Cash Disbursements Journals": those two lists narrow by it. */
const METHOD_KEY: Record<string, string> = { '': 'aw_jl_method_all', cash: 'aw_jl_method_cash', bank: 'aw_jl_method_bank' };
const METHODS = ['', 'cash', 'bank'] as const;
const hasMethods = (journal?: string) => journal === 'CRJ' || journal === 'CPJ';

/** What the server says an entry was posted from (server/src/services/journalLinks.ts sourceOfEntry). */
export type JnlSource = { kind: string; id: string; docNo: string; openable: boolean; why: string;
  /** (W1 ship) A manual entry MADE FOR this source by hand: the source is the one it books, said beside it. */
  byHand?: boolean;
  /** The Expenses register row this document is shown under, and its expense record number (blank on a record older than
   *  numbering). Absent where the document has no register row. */
  record?: { key: string; expenseNo: string };
  /** A settlement entry (its source a statement line or a cash row): the expense record numbers its payment pays. Only the
   *  entry's own read sets it (services/journalLinks.ts expenseNosOfEntry). */
  expenseNos?: string[] };
/** The expense record numbers an entry names: its document's record's, or every record its payment pays. */
const expenseNosOfSource = (s?: JnlSource | null): string[] => (s?.expenseNos?.length ? s.expenseNos : s?.record?.expenseNo ? [s.record.expenseNo] : []);
export type JnlRow = {
  id: string; entryDate: string; book: string; docNo: string; narration: string;
  pageNo: number | null; status: string; postedAt: number | null;
  /** The daily journal's own number (owner 2026-11-02). '' on a draft and on an entry posted before the numbering, whose
   *  identity stays `docNo`. `journalType` is never blank: an older entry's is read from what it is. */
  journalNo: string; journalType: string; bankAccountId: string;
  amount: number;                                  // SATANG, the ledger's own unit
  source: JnlSource;
  /** What the source document's related documents are asked by, and how many the list's own query counted (0 = no icon). */
  related?: { kind: string; id: string; count: number };
  reversedBy: { docNo: string; status: string; entryId: string; journalNo?: string } | null;
  /** It posted itself, and who saved the document it came from (owner 2026-11-02, services/autoPost.ts). */
  autoPost?: { savedBy: string };
};
export type JnlEntriesPage = { rows: JnlRow[]; total: number; page: number; limit: number };

/** One line of a voucher, as GET /journal/entries/:entryId returns it. `code` / `nameTh` / `nameEn`
 *  are the CHART's own, joined by the route — never a name composed here (the i18n iron rule). */
export type JnlLine = {
  lineNo: number; code: string; nameTh: string; nameEn: string;
  dr: number; cr: number;                          // SATANG, the ledger's own unit
  narration: string; docRef: string; partnerName: string;
};
/** One entry with its lines. Same route, same fields — nothing derived on the way in.
 *  `reverses` is an ID and nothing more: the server resolves no document number for it, which is why
 *  the detail below FETCHES that entry rather than printing the id or guessing a number from it. */
export type JnlEntryDetail = {
  id: string; clientId: string; periodId: string;
  entryDate: string; book: string; docNo: string; narration: string;
  pageNo: number | null; status: string; postedAt: number | null;
  journalNo: string; journalType: string;
  source: JnlSource;
  reversedBy: { docNo: string; status: string; entryId: string; journalNo?: string } | null;
  reverses: string | null;
  lines: JnlLine[];
  totals: { dr: number; cr: number; balanced: boolean };
  /** The payroll family this voucher belongs to — the run's accrual, a payment of its net pay, or a PND.1 / SSO
   *  remittance, AND the reversal of any of them (batch 10 R7, R8 §4.3: the route reads it through
   *  journalPost.payrollFamilyOf). A reversal's own `source` is 'reversal', so this is how the window knows; null
   *  for every other entry, absent from a server older than R7. */
  payroll?: JnlPayrollRef | null;
  /** What the reversing door would refuse of it (batch 11 R8a): its code and sentence, null when it would take it. */
  reverseRefusal?: { code: string; message: string } | null;
  /** (Batch 12 tail T4, COA-D11) What may be edited on it — the one verdict (server/src/services/draftEdit.ts draftEditOf):
   *  the row menu offers Edit by it, and the footer of a draft says where it is edited or why not. */
  edit?: JnlDraftEditVerdict | null;
  /** It posted itself, and who saved the document it came from (owner 2026-11-02, services/autoPost.ts); null when a person posted it. */
  autoPost?: { savedBy: string; at: number } | null;
};
/** GET /journal/posted and /journal/entries/:id's `payroll` (R8 brief §4.3). `runId` / `docNo` are '' for a
 *  remittance, which belongs to a month rather than a run; an R7 server sends no `kind` (its only kind was the run). */
export type JnlPayrollRef = {
  kind?: 'run' | 'payment' | 'remittance'; runId: string; docNo: string;
  paymentId?: string; payNo?: string; remittanceId?: string; remitKind?: 'pnd1' | 'sso'; year?: number; month?: number;
};

// The three the route accepts, each with its key written out in full so a grep for the key finds it.
const STATUSES = ['posted', 'draft', 'all'] as const;
const STATUS_KEY: Record<string, string> = {
  posted: 'aw_jl_status_posted', draft: 'aw_jl_status_draft', all: 'aw_jl_status_all',
  // ⛔ A real stored status (db/index.ts: 'draft' | 'posted' | 'void'), not a filter: it needs a
  // label of its own, or StatusTag below would have to invent one.
  void: 'aw_jl_status_void',
};

/** The volume's own name: its statutory Thai name where the installation has one, else the server's
 *  English label — never a name invented here. A bank volume is identified by its account number. */
export const bookTitle = (b: Book): string => {
  const name = statutoryBookName(b.kind) || b.label || b.kind;
  return b.bankAccountId ? `${name} · ${b.bankAccountId}` : name;
};

/**
 * COA-C10 — the reference document number. DISPLAY ONLY, and now deliberately so.
 *
 * ⛔ The entry's own `docNo` and the document behind it are DIFFERENT NUMBERS, and filling this cell
 * with the entry number when there is no source is how a reader reconciles to the wrong document.
 * So: the number the server resolved, or nothing. And no document type is guessed from a number's
 * prefix — the kind shown is the one `sourceOfEntry` returned.
 *
 * ⛔ THIS CELL IS NOT THE JUMP, and that is a decision rather than an omission (batch 6, COA-D1).
 * The jump lives in the voucher window below, in a card of its own, because the resolver's answer
 * is three-valued — open it, the reason it cannot be opened, or "this product has no screen for
 * that kind" (sourceDoc.tsx) — and a table cell cannot say a sentence. In the LIST, a row already
 * has one action: it opens the voucher. A second target inside that row is how a reader clicks the
 * one they did not mean, and the voucher is one click away and shows the full answer.
 * (EXCEPTION, owner 2026-11-02, "Daily Journals Structure": each journal entry links to its source document from the list. So
 * the row stopped being a target: no row click, two link cells, the Journal No. and this one. With `clientId` the number is
 * the link, through SourceDocOpen's `label` and so with its same three answers: a kind with no window prints the number with
 * the sentence on hover, and a table cell can say that much. The voucher's card below keeps the full answer in words.)
 */
const SRC_KEY: Record<string, string> = {
  invoice: 'aw_dt_invoice', tax_invoice: 'aw_dt_tax_invoice', tax_invoice_receipt: 'aw_dt_tax_invoice_receipt',
  proforma: 'aw_dt_proforma', credit_note: 'aw_dt_credit_note', debit_note: 'aw_dt_debit_note',
  receipt: 'aw_jl_src_receipt', payable: 'aw_jl_src_payable', expense_upload: 'aw_jl_src_expense_upload',
  bank_line: 'aw_jl_src_bank_line', tax_line: 'aw_jl_src_tax_line', manual: 'aw_jl_src_manual',
  depreciation: 'aw_jl_src_depreciation', reversal: 'aw_jl_src_reversal', period: 'aw_jl_src_period',
  payroll_run: 'aw_jl_src_payroll_run', payroll_remittance: 'aw_jl_src_payroll_remittance',
  purchase_note: 'aw_jl_src_purchase_note',   // batch 11 R8a: a supplier's booked credit or debit note
};

function SourceCell({ s, clientId, places }: { s: JnlSource | null | undefined; clientId?: string;
  /** On a list: a payroll run's or remittance's number goes to its place (SourceDocOpen's `places`). */
  places?: boolean }) {
  const { t } = useTranslation();
  const kind = String(s?.kind || '');
  const docNo = String(s?.docNo || '').trim();
  const why = String(s?.why || '');
  const label = SRC_KEY[kind] ? t(SRC_KEY[kind]) : '';
  // Nothing resolved at all: visibly blank, with the server's own reason on hover.
  if (!docNo && !label) return <span style={{ color: 'var(--text-tertiary)' }} title={why || undefined}>—</span>;
  return (
    <span title={why || undefined} style={{ display: 'inline-flex', flexDirection: 'column', gap: 2 }}>
      {docNo
        ? <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>{clientId ? <SourceDocOpen clientId={clientId} s={s} label={docNo} places={places} /> : docNo}</span>
        /* A hand-entered voucher, the depreciation run, a reversal, an opening/closing entry: there
           is no document, and saying which kind of entry it is — with no number — is the answer. */
        : <span style={{ color: 'var(--text-tertiary)' }}>—</span>}
      {label && <span style={{ fontSize: 10.5, color: 'var(--text-tertiary)' }}>{label}</span>}
      {/* The voucher is a manual entry made for this source by hand — it books it, as a derived entry would. */}
      {!!s?.byHand && <span data-src-by-hand style={{ fontSize: 10.5, color: 'var(--text-tertiary)' }}>{t('aw_jl_src_by_hand')}</span>}
    </span>
  );
}

/** Posted / Draft / All, in the colours the rest of the workspace uses. One function, because the
 *  list, the detail and the reversal references all have to agree on what a status looks like. */
const statusPill = (s: string) => (s === 'posted' ? pillGreen : s === 'draft' ? pillAmber : s === 'void' ? pillRed : pillGrey);
const statusKey = (s: string) => STATUS_KEY[s] || '';
/**
 * A status, as a pill, in one place — because the list, the detail and both reversal references all
 * have to agree on what one looks like.
 *
 * ⛔ IT NEVER FALLS BACK TO "Posted". `journal_entries.status` is 'draft' | 'posted' | 'void'
 * (server/src/db/index.ts, and services/journalLinks.ts says the same), so the old
 * `STATUS_KEY[s] || 'aw_jl_status_posted'` labelled a VOID voucher **Posted** — a claim about the
 * ledger that is not true, on the one screen whose whole job is to state what the ledger says. (The
 * pill was grey, which is the only reason it was not worse.) A status with no label of its own now
 * prints ITSELF: unfamiliar is the honest answer, and being renamed to a good status is not.
 */
const StatusTag = ({ s }: { s: string }) => {
  const { t } = useTranslation();
  const k = statusKey(s);
  return <span style={statusPill(s)}>{k ? t(k) : (String(s || '').trim() || '—')}</span>;
};
/** `posted_at` is unix SECONDS (journalPost.ts writes unixepoch()), printed the house way. */
const stampDay = (unix: number | null | undefined) =>
  (unix ? dmy(new Date(Number(unix) * 1000).toISOString().slice(0, 10)) : '');

/** One labelled fact of the voucher's head. */
const Fact = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div style={{ minWidth: 140, flex: '0 1 auto' }}>
    <div style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: '.04em', textTransform: 'uppercase', color: 'var(--text-tertiary)' }}>{label}</div>
    <div style={{ fontSize: 13, color: 'var(--text-primary)', marginTop: 2 }}>{children}</div>
  </div>
);

/**
 * COA-C5 / COA-C6 / COA-D3 — the voucher itself, in the full-screen window (owner 2026-10-19).
 *
 * ⛔ READ-ONLY, AND NOT BY OMISSION. A posted entry is REVERSED, never edited: the route answers
 * GET only, and a dozen reports (general ledger, trial balance, P&L, balance sheet, the DBD returns)
 * are built from `status = 'posted'`, so editing one in place would silently rewrite books already
 * printed. The customer's own screenshot of this screen has a Save button on it; the requirement
 * text beside it warns against copying that, and this window therefore has NO Save, no editable
 * field, and no control that implies either. Where a reversal IS raised is said in words at the
 * bottom (aw_jl_detail_readonly) rather than offered here as a second button — the reversal flow
 * lives on this same page, in Bookkeeping/ManualEntry.tsx, one "All journals" click away.
 *
 * ⛔ NO `dirty` PROP. The shell asks before discarding unsaved work; there is none here, and a
 * confirm on closing a read-only window is the false positive that teaches people to click through
 * warnings — the exact habit the dirty check exists to rely on.
 *
 * ⛔ BATCH 6 (COA-D1 · COA-D10 · COA-B-8) — THE SOURCE DOCUMENT IS NOW REACHABLE FROM HERE. The
 * window had a source-document column and no way to act on it; it now carries a card that opens
 * that document's own screen (CompanyWorkspace/sourceDoc.tsx), which is also the second half of the
 * interactive ledger's path: a level-2 movement opens this voucher, and this voucher opens the
 * paper. ⛔ It opens the SCREEN, not a PDF, and it STACKS — nothing behind it is closed, so no
 * typing anywhere can be lost and the shell's dirty check keeps every exit it had.
 *
 * ⛔ Rule 2, THE ONE THING THIS SCREEN MUST NEVER HIDE: both totals, and whether they agree. A
 * voucher whose two sides disagree is a broken voucher, so it does not read as a footnote — it is
 * the first thing in the window, in the error colour, naming both sides and the difference, and the
 * totals row says it again where the numbers are. The entry is still shown in FULL underneath,
 * because the broken voucher is precisely the one somebody needs to read.
 */
export function EntryDetailDialog({ entryId, onClose }: { entryId: string; onClose: () => void }) {
  const { t } = useTranslation();
  // A walk, not a jump. Opening the voucher on the other side of a reversal keeps the way back, so
  // following the relationship cannot cost the operator the entry they started from.
  const [trail, setTrail] = useState<string[]>([entryId]);
  useEffect(() => { setTrail([entryId]); }, [entryId]);
  const id = trail[trail.length - 1];
  const [d, setD] = useState<JnlEntryDetail | null>(null);
  const [err, setErr] = useState('');
  useEffect(() => {
    let alive = true;
    setD(null); setErr('');
    api.jnlEntry(id)
      .then((x: JnlEntryDetail) => { if (alive) setD(x); })
      .catch((e: any) => { if (alive) setErr(String(e?.message || e)); });
    return () => { alive = false; };
  }, [id]);

  // ⛔ THE ENTRY THIS ONE REVERSES comes back as an ID ALONE — reversalOf resolves the other
  // direction, not this one. An id is not a voucher number and must not be shown as one, and
  // deriving a number from it is Rule 3's forbidden guess, so the entry is READ: whatever number
  // appears here was printed by the server for that entry. When it cannot be read (the row is gone,
  // or belongs to a client this login may not see — the route answers one 404 for both), the window
  // says the relationship exists and names nothing.
  const reverses = String(d?.reverses || '');
  const [rev, setRev] = useState<{ id: string; docNo: string; journalNo: string; status: string } | null>(null);
  const [revErr, setRevErr] = useState('');
  useEffect(() => {
    if (!reverses) { setRev(null); setRevErr(''); return; }
    let alive = true;
    setRev(null); setRevErr('');
    api.jnlEntry(reverses)
      .then((x: JnlEntryDetail) => { if (alive) setRev({ id: String(x.id), docNo: String(x.docNo || ''), journalNo: String(x.journalNo || ''), status: String(x.status || '') }); })
      .catch((e: any) => { if (alive) setRevErr(String(e?.message || e)); });
    return () => { alive = false; };
  }, [reverses]);

  const dr = Number(d?.totals?.dr || 0);
  const cr = Number(d?.totals?.cr || 0);
  const diff = dr - cr;                                    // the server's own two numbers, subtracted
  // (Batch 12 tail T4, COA-D11) A DRAFT's footer, for a seat that writes: where it is edited (the row's ⋯ menu, Draft
  // entries) or, by the one verdict, why it is not. A read-only seat keeps the read-only words — never a pointer to a
  // control it does not have. The seat is read from the store: this window is opened from screens with no workspace frame.
  // The window itself stays read-only: no field, no Save.
  const readOnlySeat = useReadOnlySeat();
  const v = d?.edit || null;
  const whyText = v && !v.scope ? (v.code && EDIT_REF_KEY[v.code] ? t(EDIT_REF_KEY[v.code], { defaultValue: v.message }) : v.message) : '';
  const draftWords: { k: string; text: string; title: string } | null = !d || d.status !== 'draft' || readOnlySeat || !v ? null
    : v.scope === 'full' ? { k: 'full', text: t('aw_jl_detail_draft_full'), title: '' }
      : v.scope === 'review' ? { k: 'review', text: t('aw_jl_detail_draft_review'), title: '' }
        : { k: String(v.code || 'none'), text: t('aw_jl_detail_draft_none', { why: whyText }), title: v.message };
  const balanced = !!d?.totals?.balanced;
  const lines = d?.lines || [];
  const open = (next: string) => setTrail(s => [...s, next]);

  return (
    <Dialog fullscreen title={t('aw_jl_detail_title')} docNo={d ? d.docNo : ''} onClose={onClose}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {trail.length > 1 && (
          <div>
            <button onClick={() => setTrail(s => s.slice(0, -1))} style={btn}>
              <i className="ti ti-arrow-left" aria-hidden /> {t('aw_jl_detail_trail_back')}
            </button>
          </div>
        )}
        {err && <div style={errBox}>{err}</div>}
        {!d && !err && <Loader />}
        {d && (
          <>
            {/* ⛔ Rule 2. Loudest, first, and before anything can scroll it out of sight. */}
            {!balanced && (
              <div style={{ ...errBox, display: 'flex', gap: 8, alignItems: 'flex-start', fontWeight: 600 }}>
                <i className="ti ti-alert-triangle" style={{ flexShrink: 0, marginTop: 1 }} aria-hidden />
                <div>{t('aw_jl_detail_unbalanced', { dr: baht(dr), cr: baht(cr), diff: baht(Math.abs(diff)) })}</div>
              </div>
            )}

            <div style={{ ...card, display: 'flex', flexWrap: 'wrap', gap: 18 }}>
              {/* The daily journal's own number and the source document's, side by side (owner 2026-11-02): two numbers, never
                  one for the other. "—" on an entry posted before the numbering, which is known by its entry number beside it. */}
              <Fact label={t('aw_jl_journal_no')}>
                <span data-jl-journal-no style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}>{d.journalNo || '—'}</span>
              </Fact>
              <Fact label={t('aw_jl_ref_no')}><SourceCell s={d.source} clientId={d.clientId} /></Fact>
              <Fact label={t('aw_jl_journal_type')}><span data-jl-journal-type={d.journalType}>{JOURNAL_KEY[d.journalType] ? t(JOURNAL_KEY[d.journalType]) : '—'}</span></Fact>
              <Fact label={t('aw_jl_entry_no')}>
                <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}>{d.docNo}</span>
              </Fact>
              {/* ⛔ The volume's statutory Thai name where the installation has one, else the
                  server's own book key. Rule 5: never a book name invented on the screen. */}
              <Fact label={t('aw_jl_detail_book')}>{statutoryBookName(d.book) || d.book}</Fact>
              <Fact label={t('aw_jl_detail_entry_date')}>{dmy(d.entryDate)}</Fact>
              {/* Two different dates, labelled apart: the date the entry bears in the book, and the
                  moment somebody posted it. A draft has the first and not the second. */}
              <Fact label={t('aw_jl_detail_posted_at')}>{stampDay(d.postedAt) || '—'}</Fact>
              <Fact label={t('aw_col_status')}><StatusTag s={d.status} /></Fact>
              {/* The expense record's own number (2026-11-02, Automatic Expense Number), beside the supplier's above:
                  read by join through the source, so the entry's stored reference stays the supplier's. */}
              {!!expenseNosOfSource(d.source).length && (
                <Fact label={t('aw_er_col_no')}>
                  <span data-jl-expense-no style={{ fontVariantNumeric: 'tabular-nums' }}>{expenseNosOfSource(d.source).join(' · ')}</span>
                </Fact>
              )}
              <Fact label={t('aw_jl_page_no')}>
                <span style={{ fontVariantNumeric: 'tabular-nums' }}>{d.pageNo === null || d.pageNo === undefined ? '—' : d.pageNo}</span>
              </Fact>
            </div>

            {/* (Owner 2026-11-02, services/autoPost.ts) Nobody pressed Post on this one, and it says so: the person named saved the document. */}
            {!!d.autoPost && (
              <div data-jl-auto-post style={{ ...card, display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 12.5, color: 'var(--text-secondary)' }}>
                <i className="ti ti-bolt" style={{ color: 'var(--accent)', marginTop: 2, flexShrink: 0 }} aria-hidden />
                <div>{t('aw_autopost_entry', { who: d.autoPost.savedBy || '—' })}</div>
              </div>
            )}

            {/* ── COA-D1 / COA-D10: THE PAPER BEHIND THIS VOUCHER, ONE CLICK, ON SCREEN ──────────
                The reference number above is the at-a-glance fact; this is the way to the document
                itself — the same window Sales › Invoices opens on a row click, mounted over this
                one so the voucher is still behind it and Esc comes straight back.
                ⛔ The three answers (open it · the server's reason why it cannot be opened · this
                product has no screen for that kind) are decided in ONE place, sourceDoc.tsx, which
                honours the resolver's own `openable` flag and `why` text rather than offering a
                link that 404s. ⛔ The client is the SERVER's (d.clientId), never the shell's. */}
            <div style={{ ...card, display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center' }}>
              <div style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: '.04em', textTransform: 'uppercase', color: 'var(--text-tertiary)' }}>
                {t('aw_sd_title')}
              </div>
              <SourceCell s={d.source} />
              <div style={{ flex: 1, minWidth: 12 }} />
              <SourceDocOpen clientId={d.clientId} s={d.source} />
            </div>

            <div style={{ ...card }}>
              <div style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: '.04em', textTransform: 'uppercase', color: 'var(--text-tertiary)' }}>
                {t('aw_jl_narration')}
              </div>
              <div style={{ fontSize: 13, color: 'var(--text-primary)', marginTop: 3 }}>{d.narration || '—'}</div>
            </div>

            {/* ── The reversal relationship, both directions, exactly as the server resolved it ── */}
            {(d.reversedBy || reverses) && (
              <div style={{ ...card, display: 'flex', flexDirection: 'column', gap: 7 }}>
                <div style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: '.04em', textTransform: 'uppercase', color: 'var(--text-tertiary)' }}>
                  {t('aw_jl_detail_reversal')}
                </div>
                {d.reversedBy && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', fontSize: 13 }}>
                    <span>{t('aw_jl_detail_reversed_by', { docNo: d.reversedBy.journalNo || d.reversedBy.docNo })}</span>
                    <StatusTag s={d.reversedBy.status} />
                    <button onClick={() => open(String(d.reversedBy?.entryId || ''))} style={{ ...btn, padding: '3px 9px', fontSize: 11.5 }}>
                      {t('aw_jl_detail_open_entry')}
                    </button>
                  </div>
                )}
                {reverses && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', fontSize: 13 }}>
                    {rev
                      ? <>
                          <span>{t('aw_jl_detail_reverses', { docNo: rev.journalNo || rev.docNo })}</span>
                          <StatusTag s={rev.status} />
                          <button onClick={() => open(rev.id)} style={{ ...btn, padding: '3px 9px', fontSize: 11.5 }}>
                            {t('aw_jl_detail_open_entry')}
                          </button>
                        </>
                      /* The relationship is the server's; the number is not available, so none is
                         shown. The reason it could not be read rides on the title attribute. */
                      : <span title={revErr || undefined} style={{ color: revErr ? 'var(--text-secondary)' : 'var(--text-tertiary)' }}>
                          {revErr ? t('aw_jl_detail_reverses_unknown') : t('aw_jl_detail_reverses_loading')}
                        </span>}
                  </div>
                )}
              </div>
            )}

            {/* ── COA-D3: every line, with the chart's own code and name ── */}
            <div style={{ ...card, padding: 0, overflowX: 'auto' }}>
              {!lines.length ? <div style={emptyLine}>{t('aw_jl_detail_no_lines')}</div> : (
                <table style={table}>
                  <thead>
                    <tr>
                      <th style={{ ...th, textAlign: 'right' }}>{t('aw_jl_detail_line_no')}</th>
                      <th style={th}>{t('aw_jl_detail_account')}</th>
                      <th style={th}>{t('aw_jl_narration')}</th>
                      <th style={th}>{t('aw_jl_detail_doc_ref')}</th>
                      <th style={th}>{t('aw_jl_detail_partner')}</th>
                      <th style={{ ...th, textAlign: 'right' }}>{t('aw_jl_detail_dr')}</th>
                      <th style={{ ...th, textAlign: 'right' }}>{t('aw_jl_detail_cr')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lines.map((l, i) => (
                      <tr key={`${l.lineNo}-${i}`}>
                        <td style={num}>{l.lineNo}</td>
                        <td style={{ ...td, whiteSpace: 'nowrap' }}>
                          <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}>{l.code || '—'}</span>
                          {/* Both names the chart holds, neither translated: a statutory Thai
                              account name is the name, and the English one is what the chart says. */}
                          <span style={{ display: 'block', fontSize: 11.5, color: 'var(--text-secondary)' }}>{l.nameTh}</span>
                          {l.nameEn && <span style={{ display: 'block', fontSize: 11, color: 'var(--text-tertiary)' }}>{l.nameEn}</span>}
                        </td>
                        <td style={td}>{l.narration}</td>
                        <td style={{ ...td, whiteSpace: 'nowrap' }}>{l.docRef}</td>
                        <td style={td}>{l.partnerName}</td>
                        <td style={num}>{l.dr ? baht(l.dr) : ''}</td>
                        <td style={num}>{l.cr ? baht(l.cr) : ''}</td>
                      </tr>
                    ))}
                  </tbody>
                  {/* ⛔ Rule 2 again, where the numbers are. The totals are the SERVER's own two
                      sums, and `balanced` is its own comparison of them — this row reports, it does
                      not recompute, so the screen and the posting check cannot disagree. */}
                  <tfoot>
                    <tr>
                      <td style={{ ...td, fontWeight: 800, borderTop: '2px solid var(--border-strong)' }} colSpan={5}>
                        {t('aw_jl_detail_totals')}
                      </td>
                      <td style={{ ...num, fontWeight: 800, borderTop: '2px solid var(--border-strong)', color: balanced ? 'var(--text-primary)' : '#B42318' }}>{baht(dr)}</td>
                      <td style={{ ...num, fontWeight: 800, borderTop: '2px solid var(--border-strong)', color: balanced ? 'var(--text-primary)' : '#B42318' }}>{baht(cr)}</td>
                    </tr>
                    <tr>
                      <td style={{ ...td, borderBottom: 'none' }} colSpan={7}>
                        {balanced
                          ? <span style={pillGreen}><i className="ti ti-check" aria-hidden /> {t('aw_jl_detail_balanced')}</span>
                          : <span style={pillRed}><i className="ti ti-alert-triangle" aria-hidden /> {t('aw_jl_detail_out_by', { diff: baht(Math.abs(diff)) })}</span>}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              )}
            </div>

            {/* The voucher's supporting files and what it is linked to (owner 2026-11-02): the general journal, the adjusting entries and every other entry alike. Adding a file writes
                nothing here, so a posted entry cannot move. The client is the SERVER's, as above. */}
            <RecordEvidence clientId={d.clientId} kind="journal_entry" id={String(d.id)} />

            {/* Why there is no Save — said where somebody looking for one will read it. ⛔ A payroll voucher
                (the accrual or its reversal, batch 10 R7; a payment or a remittance and their reversals, R8) is
                NOT reversed on the corrections panel: the ledger door refuses it there, so pointing at that panel
                would send the reader to a 409. It says where it is reversed instead — its run, the payment in
                that run's window, or the remittance in Payroll › Payroll List › Remittances. */}
            <div style={{ ...card, padding: '9px 12px', display: 'flex', gap: 8, alignItems: 'flex-start' }}
              data-jl-readonly={d.payroll ? 'payroll' : draftWords ? `draft-${draftWords.k}` : 'ledger'} data-jl-payroll={d.payroll ? (d.payroll.kind || 'run') : undefined}>
              <i className="ti ti-lock" style={{ color: 'var(--accent)', flexShrink: 0, marginTop: 1 }} aria-hidden />
              <div style={note} title={draftWords?.title || undefined}>{d.payroll ? <PayrollFooter p={d.payroll} /> : draftWords ? draftWords.text : t('aw_jl_detail_readonly')}</div>
            </div>
          </>
        )}
      </div>
    </Dialog>
  );
}

/** Why a draft is not edited, by the verdict's code — each key written in full so a grep finds it; a code this build does
 *  not know is said in the server's words. Read by the row menu and the voucher's footer here, by Draft entries and by the editor (Bookkeeping/DraftEditDialog.tsx) —
 *  one map, in the journal's own file, beside the other aw_jl_ words. */
export const EDIT_REF_KEY: Record<string, string> = {
  not_found: 'aw_jl_edit_ref_not_found', not_draft: 'aw_jl_edit_ref_not_draft', payroll: 'aw_jl_edit_ref_payroll',
  period_closed: 'aw_jl_edit_ref_period_closed', reversal_locked: 'aw_jl_edit_ref_reversal_locked', hand_source: 'aw_jl_edit_ref_hand_source',
  stale: 'aw_jl_edit_ref_stale', no_free_leg: 'aw_jl_edit_ref_no_free_leg', generated_settlement: 'aw_jl_edit_ref_generated_settlement',
  generated_register: 'aw_jl_edit_ref_generated_register', unknown_origin: 'aw_jl_edit_ref_unknown_origin',
};
/** The statutory names stay Thai in every language (the R6 house rule), so they are written here, never in i18n. */
const REMIT_KIND_WORD: Record<string, string> = { pnd1: 'ภ.ง.ด.1', sso: 'สปส.' };

/** The read-only footer's sentence for a payroll voucher, by what in payroll owns it (R8 §4.3 `payroll.kind`): the
 *  run (R7's words), a payment of that run, or a remittance — which has no run, so it names its month beside the
 *  statutory name instead of a run number. */
function PayrollFooter({ p }: { p: JnlPayrollRef }) {
  const { t } = useTranslation();
  if (p.kind === 'payment') return <>{t('aw_jl_detail_payroll_pay', { payNo: p.payNo || '', docNo: p.docNo })}</>;
  if (p.kind === 'remittance') {
    const mmYyyy = p.year && p.month ? `${String(p.month).padStart(2, '0')}/${p.year}` : '';
    return (
      <>
        <span style={{ fontWeight: 700, marginRight: 6 }}>{REMIT_KIND_WORD[String(p.remitKind || '')] || ''}</span>
        {t('aw_jl_detail_payroll_remit', { month: mmYyyy })}
      </>
    );
  }
  return <>{t('aw_jl_detail_payroll', { docNo: p.docNo })}</>;
}

/**
 * COA-C8 — the row's own actions, as a menu: View details · Edit · Reverse…, by status AND permission (batch 12 tail T4).
 *
 * ⛔ ONLY WHAT WOULD BE TAKEN, AND WHY NOT IN WORDS (TL-24, 「工作台不留灰菜单」). For a seat that writes (not a read-only
 * seat, and holding accounting_books), opening the menu reads the entry once (GET /journal/entries/:id — a loader line
 * until it answers) and then offers:
 *   · View details, always;
 *   · Edit on a DRAFT whose verdict lets something change (`edit.scope` — server/src/services/draftEdit.ts draftEditOf) —
 *     the full-screen editor (Bookkeeping/DraftEditDialog.tsx);
 *   · Reverse… on a POSTED entry the reversing door would take (`reverseRefusal` null, no payroll owner, an open period) —
 *     the reason card (reverseReason.tsx) and the door with it (api.jnlReverse), raising a reversal DRAFT.
 * Where one of the two is withheld, ONE plain sentence says why — never a disabled item, never a button: the verdict's
 * words (EDIT_REF_KEY), the reversing door's (REV_REF_KEY, docEntries.tsx), payroll's owner (PayrollFooter), or a closed
 * period's (O8: a posted entry of a closed period is not reversed from here). The server's own sentence rides on hover.
 * ⛔ A read-only seat is offered View details only, and its menu reads NOTHING (no verdict GET): nothing it could be
 * offered depends on it.
 * (EXCEPTION, owner 2026-11-02: the Sales lists' ⋯ menu, RowMenu.tsx, shows a withheld action greyed with its sentence,
 * because that requirement names the actions a document has. This menu keeps TL-24.)
 *
 * ⛔ FIXED off the trigger's own rect, the house precedent (ClientPortal RowMenu): this table sits
 * in a card with `overflowX: auto`, which would clip an absolutely-positioned panel, and the last
 * row's menu would fall off the bottom of the viewport.
 */
function RowActions({ ctx, row, writes, onView, onEdit, onReverse }: {
  ctx: AwCtx; row: JnlRow; writes: boolean;
  onView: () => void; onEdit: () => void; onReverse: () => void;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const btnRef = React.useRef<HTMLButtonElement | null>(null);
  const panelRef = React.useRef<HTMLDivElement | null>(null);
  // The entry as the server answers it, read once per opening of the menu (writing seats only).
  const [info, setInfo] = useState<JnlEntryDetail | null>(null);
  const [infoErr, setInfoErr] = useState('');

  useEffect(() => {
    if (!open) return;
    const away = (e: MouseEvent) => {
      const n = e.target as Node;
      if (!btnRef.current?.contains(n) && !panelRef.current?.contains(n)) setOpen(false);
    };
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.stopPropagation(); setOpen(false); } };
    // A panel pinned to a rect must not linger where the trigger no longer is.
    const bail = () => setOpen(false);
    document.addEventListener('mousedown', away);
    document.addEventListener('keydown', esc);
    window.addEventListener('scroll', bail, true);
    window.addEventListener('resize', bail);
    return () => {
      document.removeEventListener('mousedown', away);
      document.removeEventListener('keydown', esc);
      window.removeEventListener('scroll', bail, true);
      window.removeEventListener('resize', bail);
    };
  }, [open]);

  useEffect(() => {
    if (!open || !writes) return;
    let alive = true;
    setInfo(null); setInfoErr('');
    api.jnlEntry(row.id)
      .then((x: JnlEntryDetail) => { if (alive) setInfo(x); })
      .catch((e: any) => { if (alive) setInfoErr(String(e?.message || e)); });
    return () => { alive = false; };
  }, [open, writes, row.id]);

  const item: React.CSSProperties = { ...btn, width: '100%', justifyContent: 'flex-start', border: 'none', background: 'transparent', textAlign: 'left' };
  const why: React.CSSProperties = { fontSize: 11.5, color: 'var(--text-secondary)', padding: '5px 9px', lineHeight: 1.45, maxWidth: 300 };
  const pick = (f: () => void) => (e: React.MouseEvent) => { e.stopPropagation(); setOpen(false); f(); };

  // What the entry offers, once read: by status, then by the verdict / the reversing door / the period.
  const lines: React.ReactNode[] = [];
  if (writes && open) {
    if (!info) {
      lines.push(infoErr
        ? <div key="err" data-jl-menu-why="error" style={why}>{infoErr}</div>
        : <div key="load" data-jl-menu-loading style={why}><i className="ti ti-loader" aria-hidden /> {t('aw_jl_menu_loading')}</div>);
    } else if (info.status === 'draft') {
      const v = info.edit;
      if (v?.scope) {
        lines.push(<button key="edit" type="button" data-jl-menu="edit" onClick={pick(onEdit)} style={item}><i className="ti ti-pencil" aria-hidden /> {t('aw_jl_menu_edit')}</button>);
      } else if (info.payroll) {
        lines.push(<div key="why" data-jl-menu-why="payroll" style={why}><PayrollFooter p={info.payroll} /></div>);
      } else if (v) {
        lines.push(<div key="why" data-jl-menu-why={v.code || ''} title={v.message} style={why}>
          {v.code && EDIT_REF_KEY[v.code] ? t(EDIT_REF_KEY[v.code], { defaultValue: v.message }) : v.message}</div>);
      }
    } else if (info.status === 'posted') {
      const rr = info.reverseRefusal;
      if (info.payroll) {
        lines.push(<div key="why" data-jl-menu-why="payroll" style={why}><PayrollFooter p={info.payroll} /></div>);
      } else if (rr) {
        lines.push(<div key="why" data-jl-menu-why={rr.code} title={rr.message} style={why}>
          {REV_REF_KEY[rr.code] ? t(REV_REF_KEY[rr.code], { docNo: info.docNo }) : rr.message}</div>);
      } else if (ctx.periodClosed) {
        lines.push(<div key="why" data-jl-menu-why="period_closed" style={why}>{t('aw_jl_menu_reverse_closed')}</div>);
      } else {
        lines.push(<button key="rev" type="button" data-jl-menu="reverse" onClick={pick(onReverse)} style={item}><i className="ti ti-arrow-back-up" aria-hidden /> {t('aw_jl_menu_reverse')}</button>);
      }
    } else if (info.edit && !info.edit.scope && info.edit.code && EDIT_REF_KEY[info.edit.code]) {
      lines.push(<div key="why" data-jl-menu-why={info.edit.code} title={info.edit.message} style={why}>{t(EDIT_REF_KEY[info.edit.code], { defaultValue: info.edit.message })}</div>);
    }
  }

  return (
    <>
      <button ref={btnRef} type="button" title={t('aw_col_actions')} aria-label={t('aw_col_actions')}
        onClick={e => {
          e.stopPropagation();
          const r = btnRef.current?.getBoundingClientRect();
          setPos(r ? { top: r.bottom + 4, left: Math.max(8, r.right - 240) } : { top: 8, left: 8 });
          setOpen(o => !o);
        }}
        style={{ ...btn, padding: '3px 7px' }}>
        <i className="ti ti-dots-vertical" aria-hidden />
      </button>
      {open && pos && (
        <div ref={panelRef} onClick={e => e.stopPropagation()} data-jl-menu-panel style={{
          position: 'fixed', top: pos.top, left: pos.left, zIndex: 10003, minWidth: 190, maxWidth: 320,
          background: 'var(--bg-surface)', border: '1px solid var(--border-strong)', borderRadius: 10,
          boxShadow: 'var(--shadow-md)', padding: 5,
        }}>
          <button type="button" data-jl-menu="view" onClick={pick(onView)} style={item}>
            <i className="ti ti-file-text" aria-hidden /> {t('aw_jl_detail_view')}
          </button>
          {lines}
        </div>
      )}
    </>
  );
}

/** The statutory volume, unfiltered, opened in a tab. Every download button in this repo spins. */
function StatutoryPdfBtn({ ctx, book }: { ctx: AwCtx; book: Book }) {
  const { t } = useTranslation();
  return (
    <DlBtn style={{ ...btn, borderColor: 'var(--accent)', color: 'var(--accent-text)' }}
      title={t('aw_jl_statutory_title')}
      onRun={async () => openBlob(await api.ledgerBookUrl(`journal/${book.kind}`, ctx.periodId,
        book.bankAccountId ? { bankAccountId: book.bankAccountId } : {}))}>
      <i className="ti ti-book" aria-hidden /> {statutoryBookName(book.kind) || book.label || book.kind} (PDF)
      <span style={{ color: 'var(--text-tertiary)', fontWeight: 500 }}>· {t('aw_jl_statutory')}</span>
    </DlBtn>
  );
}

/** A link cell's button: looks like the workspace's other number links, and stays a button (the jump's own rule, sourceDoc.tsx). */
const linkCell: React.CSSProperties = { background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'var(--accent)', fontWeight: 700, fontSize: 'inherit', fontVariantNumeric: 'tabular-nums', textAlign: 'left' };

/** One book's entries, or one daily journal's: filters, the page, the honest total, and the export exits. A daily journal is
 *  not a statutory book, so it has this view's two exports and no book button; the book button stays on the statutory cards. */
export function JournalBookView({ ctx, book, onBack }: {
  ctx: AwCtx; book: Book; onBack: () => void;
}) {
  const { t } = useTranslation();
  const [status, setStatus] = useState<string>('posted');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [q, setQ] = useState('');
  const [qd, setQd] = useState('');
  // A journal number, or part of one: its own box, beside the search that also reads it (owner 2026-11-02, "searching and filtering").
  const [no, setNo] = useState('');
  const [noD, setNoD] = useState('');
  const [method, setMethod] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(getPageSize());
  const [data, setData] = useState<JnlEntriesPage | null>(null);
  const [err, setErr] = useState('');
  // COA-C5: which voucher is open in the full-screen window. '' = none.
  const [detail, setDetail] = useState('');
  // (Batch 12 tail T4, COA-C8) The row menu's two doors: the draft open in the editor, the posted entry asked for a reason.
  // A seat that writes: not a read-only seat, and holding the books module (a books seat sees View details only).
  const writes = !isReadOnlySeat(ctx) && (ctx.me?.modules || []).includes('accounting_books');
  const [editing, setEditing] = useState('');
  const [reversing, setReversing] = useState<JnlRow | null>(null);
  const [done, setDone] = useState('');
  const [refusal, setRefusal] = useState('');

  // ⛔ The search box sends `q` to the SERVER. The route also searches the LINES' doc_ref and
  // partner_name, which a filter over the 100 rows in hand cannot reach: an accountant typing a
  // counterparty's name into a client-side filter would silently get nothing back.
  useEffect(() => { const h = setTimeout(() => { setQd(q); setNoD(no); setPage(1); }, 220); return () => clearTimeout(h); }, [q, no]);

  // One object, three exits (list · Excel · PDF) — Rule 3's shared contract, see api.jnlViewQs.
  // ⛔ bankAccountId rides in the view, so the list, the Excel and the PDF all read ONE volume — the
  // card's own account. This screen used to REFUSE to open a bank book when the client had more than
  // one account, because GET /journal/entries had no such parameter and would have merged them: the
  // card said one account number and the rows underneath were two accounts', while the statutory PDF
  // behind the same card showed one. The refusal was the honest answer to that; the parameter is the
  // fix, so the refusal goes.
  // (Owner 2026-11-02) A daily journal's card reads across the books, so its `book` is the cash-or-bank choice on its own list.
  const view: JnlView = useMemo(
    () => ({ book: book.journal ? method : book.kind, status, from, to, q: qd, bankAccountId: book.bankAccountId, journal: book.journal || '', journalNo: noD }),
    [book.kind, book.journal, book.bankAccountId, method, status, from, to, qd, noD]);

  useEffect(() => {
    let alive = true;
    api.jnlEntries(ctx.clientId, ctx.periodId, view, page, limit)
      .then((d: JnlEntriesPage) => { if (alive) { setData(d); setErr(''); } })
      .catch((e: any) => { if (alive) { setData({ rows: [], total: 0, page: 1, limit }); setErr(String(e?.message || e)); } });
    return () => { alive = false; };
  }, [ctx.clientId, ctx.periodId, ctx.reload, view, page, limit]);

  const rows = data?.rows || [];
  const shownFrom = data && data.total ? (data.page - 1) * (data.limit || limit) + 1 : 0;
  const shownTo = data ? shownFrom + rows.length - 1 : 0;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {/* row 1: which volume, and the way back to the five cards */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <button onClick={onBack} style={btn}><i className="ti ti-arrow-left" aria-hidden /> {t('aw_jl_back')}</button>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 800, color: 'var(--text-primary)' }}>{book.journal && JOURNAL_KEY[book.journal] ? t(JOURNAL_KEY[book.journal]) : bookTitle(book)}</div>
          <div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>
            {book.label} · {ctx.period ? `${dmy(ctx.period.start_date)} – ${dmy(ctx.period.end_date)}` : ''}
          </div>
        </div>
        <div style={{ flex: 1 }} />
        {/* ── Exit 1: the statutory book. No filters reach it. Opens in a tab. ── */}
        {!book.journal && <StatutoryPdfBtn ctx={ctx} book={book} />}
        {!book.journal && <div style={{ width: 1, height: 26, background: 'var(--border-strong)' }} aria-hidden />}
        {/* ── Exit 2: this view, with its filters. Downloads. ── */}
        <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: '.04em', textTransform: 'uppercase', color: 'var(--text-tertiary)' }}>
          {t('aw_jl_this_view')}
        </span>
        <DlBtn style={btn} title={t('aw_jl_view_title')} onRun={() => api.jnlViewXlsx(ctx.periodId, view)}>
          <i className="ti ti-file-spreadsheet" aria-hidden /> {t('aw_jl_view_xlsx')}
        </DlBtn>
        <DlBtn style={btn} title={t('aw_jl_view_title')} onRun={() => api.jnlViewPdf(ctx.periodId, view)}>
          <i className="ti ti-file-type-pdf" aria-hidden /> {t('aw_jl_view_pdf')}
        </DlBtn>
      </div>
      {/* Which file is which, said in words rather than left to the icons. */}
      <div style={note}>{book.journal ? t('aw_jl_one_exit') : t('aw_jl_two_exits')}</div>

      {/* row 2: the filters worth having — status, a date range inside the period, and the search box */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', gap: 6 }}>
          {STATUSES.map(s => (
            <button key={s} onClick={() => { setStatus(s); setPage(1); }}
              style={s === status ? { ...chip, ...chipOn } : chip}>{t(STATUS_KEY[s])}</button>
          ))}
        </div>
        {hasMethods(book.journal) && (
          <div style={{ display: 'flex', gap: 6 }}>
            {METHODS.map(m => (
              <button key={m} data-jl-method={m} onClick={() => { setMethod(m); setPage(1); }}
                style={m === method ? { ...chip, ...chipOn } : chip}>{t(METHOD_KEY[m])}</button>
            ))}
          </div>
        )}
        <div style={{ width: 1, height: 22, background: 'var(--border-strong)' }} aria-hidden />
        <input type="date" value={from} min={ctx.period?.start_date} max={ctx.period?.end_date}
          onChange={e => { setFrom(e.target.value); setPage(1); }} title={t('aw_jl_from')} style={inp} />
        <span style={{ color: 'var(--text-tertiary)' }}>–</span>
        <input type="date" value={to} min={ctx.period?.start_date} max={ctx.period?.end_date}
          onChange={e => { setTo(e.target.value); setPage(1); }} title={t('aw_jl_to')} style={inp} />
        {(from || to) && (
          <button onClick={() => { setFrom(''); setTo(''); setPage(1); }} title={t('aw_jl_clear_dates')} style={{ ...btn, padding: '5px 8px' }}>
            <i className="ti ti-x" aria-hidden />
          </button>
        )}
        <input value={no} onChange={e => setNo(e.target.value)} data-jl-no-filter placeholder={t('aw_jl_no_ph')} aria-label={t('aw_jl_journal_no')} style={{ ...inp, flex: '0 1 190px' }} />
        <input value={q} onChange={e => setQ(e.target.value)} placeholder={t('aw_jl_search_ph')} style={{ ...inp, flex: '1 1 220px', maxWidth: 380 }} />
      </div>

      {/* ⛔ The total, always, and which slice of it is on screen. A screen that renders 100 of 871
          entries without saying so is how an accountant concludes the books are short. */}
      {data && rows.length > 0 && (
        <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
          {t('aw_jl_showing', { from: shownFrom, to: shownTo, total: data.total })}
        </div>
      )}

      {err && <div style={errBox}>{err}</div>}
      {done && <div data-jl-done style={{ ...card, padding: '8px 12px', fontSize: 12.5, color: '#166534', background: '#E8F6EE', borderColor: '#BBE5CB' }}>{done}</div>}
      {refusal && <div role="alert" data-jl-refusal style={errBox}>{refusal}</div>}

      {/* ⛔ Rule 4, where somebody will actually read it: a journal holds posted entries only. */}
      <div style={{ ...card, padding: '9px 12px', display: 'flex', gap: 8, alignItems: 'flex-start' }}>
        <i className="ti ti-info-circle" style={{ color: 'var(--accent)', flexShrink: 0, marginTop: 1 }} aria-hidden />
        <div style={note}>{t('aw_jl_posted_only')}</div>
      </div>

      {(
        <>
          <div style={{ ...card, padding: 0, overflowX: 'auto' }}>
            {data === null ? <Loader /> : !rows.length ? <div style={emptyLine}>{t('aw_jl_none')}</div> : (
              <table style={table}>
                <thead>
                  <tr>
                    <th style={th}>{t('aw_col_date')}</th>
                    <th style={th}>{t('aw_jl_journal_no')}</th>
                    <th style={th}>{t('aw_jl_ref_no')}</th>
                    {hasMethods(book.journal) && <th style={th}>{t('aw_jl_col_method')}</th>}
                    <th style={th}>{t('aw_jl_narration')}</th>
                    <th style={{ ...th, textAlign: 'right' }}>{t('aw_dd_amount')}</th>
                    <th style={{ ...th, textAlign: 'right' }}>{t('aw_jl_page_no')}</th>
                    <th style={th}>{t('aw_col_status')}</th>
                    <th style={th}>{t('aw_jl_reversed_by')}</th>
                    <th style={{ ...th, textAlign: 'right' }}>{t('aw_col_actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {/* COA-C5: the voucher opens from the list — the thing the customer said this screen
                      would not do — and the menu beside it offers the same by name.
                      (EXCEPTION, owner 2026-11-02: from the Journal No., not from the row. The source document's
                      number is a link now, and a row that is also a target is how a reader clicks the one they did
                      not mean. An older entry reads "—" as its number and keeps its entry number under it, as a link too.) */}
                  {rows.map(r => (
                    <tr key={r.id}>
                      <td style={{ ...td, whiteSpace: 'nowrap' }}>{dmy(r.entryDate)}</td>
                      <NoCell
                        no={<button type="button" data-jl-open={r.id} onClick={() => setDetail(r.id)} title={t('aw_jl_detail_view')} style={linkCell}>{r.journalNo || '—'}</button>}
                        sub={r.docNo ? <button type="button" data-jl-open-entry={r.id} onClick={() => setDetail(r.id)} title={t('aw_jl_detail_view')} style={{ ...linkCell, fontWeight: 400, color: 'inherit' }}>{r.docNo}</button> : undefined}
                        // (Owner 2026-11-02, "view journal entries and their related documents") The source document's chain, as the document lists offer it.
                        rel={r.related && r.source.openable && hasRecordWindow(r.source.kind) ? { ctx, kind: r.related.kind, id: r.related.id, docNo: r.source.docNo, count: r.related.count } : undefined} />
                      <td style={{ ...td, whiteSpace: 'nowrap' }}><SourceCell s={r.source} clientId={ctx.clientId} places /></td>
                      {hasMethods(book.journal) && (
                        <td style={{ ...td, whiteSpace: 'nowrap' }} data-jl-method-cell={r.book}>
                          {METHOD_KEY[r.book] ? t(METHOD_KEY[r.book]) : r.book}
                          {!!r.bankAccountId && <div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>{r.bankAccountId}</div>}
                        </td>
                      )}
                      <td style={td}>{r.narration}</td>
                      <td style={num}>{baht(r.amount)}</td>
                      <td style={num}>{r.pageNo === null || r.pageNo === undefined ? '' : r.pageNo}</td>
                      <td style={td}>
                        <StatusTag s={r.status} />
                        {!!r.autoPost && <i data-jl-row-auto-post className="ti ti-bolt" title={t('aw_autopost_entry', { who: r.autoPost.savedBy || '—' })} style={{ marginLeft: 5, fontSize: 12, color: 'var(--accent)' }} aria-label={t('aw_autopost_entry', { who: r.autoPost.savedBy || '—' })} />}
                      </td>
                      <td style={{ ...td, whiteSpace: 'nowrap' }}>
                        {r.reversedBy
                          ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                              <span style={{ fontVariantNumeric: 'tabular-nums' }}>{r.reversedBy.journalNo || r.reversedBy.docNo}</span>
                              <StatusTag s={r.reversedBy.status} />
                            </span>
                          : ''}
                      </td>
                      <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                        <RowActions ctx={ctx} row={r} writes={writes} onView={() => setDetail(r.id)}
                          onEdit={() => { setDone(''); setRefusal(''); setEditing(r.id); }}
                          onReverse={() => { setDone(''); setRefusal(''); setReversing(r); }} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
          {/* Pager driven by the SERVER's echoed limit, the workspace convention (Documents.tsx). */}
          {data && <Pagination page={page} total={data.total} limit={data.limit || limit} onPage={setPage} onLimit={n => { setLimit(n); setPage(1); }} />}
        </>
      )}

      {/* COA-C6: the voucher fills the viewport, in the shell shipped with the document windows —
          not a second one. Read-only, so no `dirty` flag reaches it. */}
      {detail && <EntryDetailDialog entryId={detail} onClose={() => setDetail('')} />}
      {/* (Batch 12 tail T4, COA-D11) Edit from the row menu: the full-screen editor; a save reads the list again. */}
      {editing && (
        <DraftEditDialog entryId={editing} onClose={() => setEditing('')}
          onSaved={(m: string) => { setEditing(''); setDone(m); ctx.bump(); }}
          onChanged={() => ctx.bump()} />
      )}
      {/* (Batch 12 tail T4, COA-C8) Reverse… from the row menu: the reason, then the reversing door — a reversal DRAFT is
          raised and said. A refusal (the books decided it meanwhile) closes the card, says why and reads the list again. */}
      {reversing && (
        <ReverseReasonDialog docNo={reversing.docNo} onClose={() => setReversing(null)}
          onSubmit={async (reason: string) => {
            const rv: any = await api.jnlReverse(reversing.id, reason);
            setReversing(null); setDone(t('aw_jl_reverse_raised', { docNo: String(rv?.docNo || '') })); ctx.bump();
          }}
          onRefused={(m: string) => { setReversing(null); setRefusal(m); ctx.bump(); }} />
      )}
    </div>
  );
}
